import { UserDataAdapter } from "../../adpters/UserDataAdapter";
import axios from "axios";
import * as FileSaver from "file-saver";

import { Assistant } from "../../../gateway/Assistant/Assistant";
import { HttpAdapter } from "../../../main/adapters/HttpAdapter";
import { GetCompaniesByFilterUsecase } from "../../../usecases";
import { DocumentUtils } from "../../../utils/document/document.utils";

export class ImportPGDAS {
    HttpAdapter = new HttpAdapter();
    Assistant = new Assistant();
    bucketPGDAS = `${publicSettings.aws.buckets.pgdas}`;
    baseUrlSimplesNacional = `${publicSettings.aws.baseUrls.dataTimeseries}/simples-nacional`;
    baseUrlDataTimeseries = `${publicSettings.aws.baseUrls.dataTimeseries}`;

    simplesToBackup = [];
    isDuplicateCompanies = false;

    constructor({ Company } = {}) {
        this.backupPGDAS = new BackupPGDAS();
        this.Company = Company;
    }

    async import({
        files = [],
        companyId = "",
        accountingId = "",
        originalFiles = [],
        isCreated = false
    }) {
        if (!files.length || !accountingId) return false;
        const simplesNacionais = await this.extractPDFsData({
            files,
            originalFiles,
            companyId
        });

        if (isCreated) {
            const companiesWithSimplesNacional = await this._managerCompanies(
                accountingId,
                simplesNacionais
            );

            for (let companySimplesNacional of companiesWithSimplesNacional) {
                console.info("[PGDAS] Importando dados");

                await this._startImportDocuments({
                    simplesNacionais: companySimplesNacional.simplesNacionais,
                    companyId: companySimplesNacional.companyId,
                    accountingId
                });
            }

            console.info("[PGDAS] Dados importado");
            return companiesWithSimplesNacional;
        } else {
            const response = await this._startImportDocuments({
                simplesNacionais,
                companyId,
                accountingId
            });

            return response;
        }
    }
    async _startImportDocuments({ simplesNacionais, companyId, accountingId }) {
        if (!simplesNacionais?.length) return;
        const [response] = await Promise.all([
            this.callImporter({
                simplesNacionais,
                companyId,
                accountingId
            })
        ]);

        this.backupFiles({ companyId, accountingId });

        return response;
    }

    async _managerCompanies(accountingId, simplesNacionais) {
        const identifiers = this.getDocumentsCompanies(simplesNacionais);
        const getCompaniesByFilterUsecase = new GetCompaniesByFilterUsecase();
        const companiesExists = await getCompaniesByFilterUsecase.execute({
            accountingId,
            filter: {
                documents: identifiers
            }
        });
        const documentsNotExists = identifiers.filter(
            (i) => !companiesExists.map((c) => c.document).includes(i)
        );

        if (documentsNotExists.length && companiesExists.length) {
            this.isDuplicateCompanies = true;
        }

        const response = [];

        if (documentsNotExists.length) {
            const companies = documentsNotExists.map((c) => ({
                document: c,
                status: "ATIVO"
            }));

            const companiesCreated = await this.Company.createNewCompanies({
                accountingId,
                companies
            });

            companiesCreated.forEach((c) => {
                const companiesSimplesNacional = simplesNacionais.filter(
                    (s) =>
                        DocumentUtils.clean(s.identifier.cnpj) === c.document
                );

                response.push({
                    companyId: c._id,
                    simplesNacionais: companiesSimplesNacional
                });
            });
        } else {
            response.push({ companiesHasCreated: true });
        }

        return response;
    }

    hasDuplicateImportCompanies() {
        if (this.isDuplicateCompanies) {
            this.isDuplicateCompanies = false;
            return true;
        } else return false;
    }

    async callImporter({
        simplesNacionais = [],
        companyId = "",
        accountingId = ""
    }) {
        return await this.HttpAdapter.call(
            `${this.baseUrlSimplesNacional}/import-documents`,
            {
                documents: simplesNacionais,
                companyId,
                accountingId,
                userId: new UserDataAdapter().user()?._id
            }
        );
    }

    async extractPDFsData({ files = [], companyId }) {
        const promiseAll = [];
        while (files.length) {
            const fileDataPart = files.splice(0, 3);
            const promisse = new Promise((resolve) =>
                resolve(this.extractPGDASData(fileDataPart, companyId))
            );
            promiseAll.push(promisse);
        }

        const documentsList = await Promise.all(promiseAll);

        if (documentsList.length) {
            let simplesNacionais = [];
            documentsList.forEach((documents) => {
                simplesNacionais.push(...documents);
            });
            return simplesNacionais;
        }
    }

    extractPGDASData = async (pdfsBase64, companyId) => {
        const documents = await this.callExtractText(pdfsBase64, companyId);
        if (!documents) throw new Error("Error extracting data from pdf");

        documents.forEach((document, index) => {
            this.pushToSimplesToBackup({
                simplesNacional: document,
                file: pdfsBase64[index]
            });
        });

        return documents;
    };

    pushToSimplesToBackup({ simplesNacional = {}, file = "" }) {
        this.simplesToBackup.push({
            ...simplesNacional,
            file
        });
    }

    async callExtractText(pdfsBase64, companyId) {
        try {
            const {
                response
            } = await this.HttpAdapter.call(
                `${this.baseUrlSimplesNacional}/extract-documents`,
                { pdfsBase64, companyId }
            );
            return response;
        } catch (error) {
            return null;
        }
    }

    async removeAll({ companyId, source, options } = {}) {
        if (!companyId || !source) throw new Error("Invalid params.");

        return await this.HttpAdapter.call(
            `${this.baseUrlDataTimeseries}/serie/remove-by-companyid-source`,
            { companyId, source, options }
        );
    }

    async saveBackupPGDAS({
        companyId = "",
        accountingId = "",
        filesToSave = []
    } = {}) {
        const details = {
            type: "application/pdf",
            source: "simplesNacional"
        };

        let count = 0;

        const save = async (backup) => {
            if (filesToSave.length === count) {
                return true;
            }

            const [_, month, year] = getPGDASDate(backup);
            const snDetails = {
                ...details,
                date: { year, month },
                originalname: `PGDAS ${year}-${month}.pdf`
            };

            await this.Assistant.putObjectInBucket({
                bucket: this.bucketPGDAS,
                key: `${accountingId}-${companyId}-${snDetails.source}-${snDetails.date.year}-${snDetails.date.month}`,
                fileBase64: backup.file,
                contentType: snDetails.type,
                metadata: {
                    ...snDetails,
                    date: `${snDetails.date.year}-${snDetails.date.month}`,
                    contentType: snDetails.type
                }
            });

            count++;

            setTimeout(() => {
                save(filesToSave[count]);
            }, 1000);
        };

        save(filesToSave[count]);
    }

    getDocumentsCompanies(simplesNacionais) {
        return [
            ...new Set(
                simplesNacionais.map((file) =>
                    DocumentUtils.clean(file.identifier.cnpj)
                )
            )
        ];
    }

    backupFiles = async ({ companyId = "", accountingId = "" }) => {
        try {
            await this.saveBackupPGDAS({
                companyId,
                accountingId,
                filesToSave: this.simplesToBackup
            });
            this.simplesToBackup = [];
        } catch (err) {
            console.error("Error on backup", err);
        }
    };
}

export class BackupPGDAS {
    HttpAdapter = new HttpAdapter();

    async getPGDASBackup(key) {
        const assistant = new Assistant();
        const responseSignedUrl = await assistant.getSingleUsePreSignedUrl(
            key,
            publicSettings.aws.buckets.pgdas
        );
        const preSignedUrl = responseSignedUrl.signedUrl;

        try {
            const response = await axios.get(preSignedUrl, {
                responseType: "blob"
            });

            const blob = new Blob([response.data], {
                type: "application/pdf;charset=utf-8"
            });

            FileSaver.saveAs(blob, responseSignedUrl.metadata.originalname);
        } catch (error) {
            console.error("error", error);
        }
    }
}

function getPGDASDate(sn) {
    return sn.identifier.verificationPeriod
        .match(/(\d{2}\/\d{2}\/\d{4})/)[1]
        .split("/")
        .map((d) => parseInt(d));
}
